import { observable, IObservableArray, flow, toJS } from 'mobx';
import API from 'api';
import Query from 'utilities/Query';
import * as yup from 'yup';
import { action } from 'mobx/lib/mobx';
import { rootStore } from 'stores/RootStore';
import Patient from 'stores/Patient';
import ItemInfor from 'stores/ItemInfor';
import PrescriberPractice from 'stores/PrescriberPractice';

export default class Rx {
	@observable id: number;
	@observable patientID: number;
	@observable prescriberPracticeID: number;
	@observable rxDate: string;
	@observable drug: string = '';
	@observable status: string = '';
	@observable fileName: string = '';
	@observable filePath: string = '';
	@observable fileImportStatus: 'notImported' | 'imported' | 'removed';
	@observable approvalNumber: string = '';
	@observable finalisationDate: string;
	@observable created: string;
	@observable modified: string;
	@observable modifiedBy: string;
	@observable modifiedById: number;
	@observable authorityNo: string = '';
	@observable itemCode: string = '';
	@observable quantity: number;
	@observable noOfRepeats: number;
	@observable dose: string = '';
	@observable treatmentType: string = '';
	@observable authorityUniqueID: string = '';
	@observable reasonCode: string = '';
	@observable reasonText: string = '';
	@observable warning: string = '';
	@observable eye: string = '';
	@observable indication: string = '';
	@observable itemInfoID: number;
	@observable searchPrescriber: string;
	@observable searchPractice: string;
	@observable searchPatientName: string;
	@observable searchTherapeuticAreaID: number;
	@observable disabled: boolean = false;
	@observable actioned: boolean = false;
	@observable overridable: boolean = false;
	@observable Patient: Patient = new Patient();
	@observable PrescriberPractice: PrescriberPractice = new PrescriberPractice();
	@observable ItemInfo: ItemInfor = new ItemInfor();
	@observable notes: string;
	@observable questions: string;
	@observable manual: boolean = false;
	@observable fileMan1: string;
	@observable fileMan2: string;
	@observable fileMan3: string;
	@observable fileMan4: string;
	@observable fileMan5: string;
	@observable hposUploadReference: string = '';
	@observable uploadedtoHPOS: string;

	constructor(data: JSON = null) {
		if (data) this.extend(data);
	}

	extend(data: JSON) {
		Object.assign(this, data);
	}

	static ValidationSchemaNewStatus = yup.object().shape({});

	static processBulk = async (ids): Promise => {
		const res = await API.post(`/ProdaActivities/processBulk`, { items: ids });
		if (res?.status === 'NOT OK') {
			const { prodaRedirectUri, prodaClientId, prodaBaseUrl } = res;
			setTimeout(function() {
				window.location.href = `${prodaBaseUrl}/authorize?response_type=code&client_id=${prodaClientId}&redirect_uri=${prodaRedirectUri}&scope=PBSAuthorities`;
			}, 2000);
			res.redirectProda = true;
		}
		return res;
	};

	static loadById = async (id: number): Promise<Rx> => {
		let res = await API.get(
			`/Rxes/${id}?filter=${JSON.stringify({
				include: ['Patient', { PrescriberPractice: ['Practice', 'Prescriber'] }, 'ItemInfo']
			})}`
		);
		return new Rx(res);
	};

	@action
	reload = async () => {
		let res = await Rx.loadById(this.id);
		this.extend(toJS(res));
	};

	validate = async () => {
		switch (this.status) {
			case 'new':
				return await Rx.ValidationSchemaNewStatus.validate(this.toJS());
			default:
				return;
		}
	};

	static getCountByUserArea = async (therapeuticAreaID: number): Promise<any> => {
		let counts = await API.get(
			`/Rxes/countByUserArea/${rootStore.auth.user.id}/${therapeuticAreaID}`
		);
		return counts;
	};

	static load = async (query: Query): Promise<{ items: Array<Rx>, itemCount: number }> => {
		let user = rootStore.auth.user;
		let getRxsUrl = `/Rxes/byUser/${user.id}/${query.toToLpQueryUrl()}`;
		let res = await API.get(getRxsUrl);
		return { items: res.items.map(el => new Rx(el)), itemCount: res.count };
	};

	clone(): Rx {
		return new Rx(JSON.parse(JSON.stringify(this)));
	}

	update = flow(
		function*() {
			yield this.validate();
			let data = this.toJS();
			if (data.id) {
				// Update
				yield API.patch(`/Rxes/${data.id}`, data);
			} else {
				// New record
				let okData = yield API.post(`/Rxes`, data);
				this.id = okData.id;
			}
		}.bind(this)
	);

	updateRx = async (file, name) => {
		let data = this.toJS();
		if (data.id) {
			let formData = new FormData();
			formData.append('file', file, name);
			const res = await API.post('/Rxes/upload', formData, 'form-data');
			return res;
		}
	};

	setAttributes = async val => {
		let data = this.toJS();
		if (!data.id) return;
		if (!val.status) {
			val.status = this.status;
		}
		await API.patch(`/Rxes/${data.id}`, val);
	};

	static callProda = async (body): Promise => {
		const res = await API.post(`/ProdaActivities/callProda`, body);
		if (res?.status === 'NOT OK') {
			const { prodaRedirectUri, prodaClientId, prodaBaseUrl } = res;
			setTimeout(function() {
				window.location.href = `${prodaBaseUrl}/authorize?response_type=code&client_id=${prodaClientId}&redirect_uri=${prodaRedirectUri}&scope=PBSAuthorities`;
			}, 2000);
			res.redirectProda = true;
		}
		return res;
	};

	/***
	 * Converts Rx to plain JSON
	 * @returns {any}
	 */
	toJS(): Rx {
		return JSON.parse(JSON.stringify(this));
	}
}



// WEBPACK FOOTER //
// ./src/stores/Rx.js